import { cn } from '@/lib/utils';

// Small accessible on/off switch (no shadcn Switch primitive in this project). Shared by the
// settings sub-pages and the queue editor's show-to-doctor / active toggles.
export function Switch({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
        disabled ? 'cursor-not-allowed opacity-60' : '',
      )}
    >
      <span
        className={cn(
          'inline-block size-5 transform rounded-full bg-background shadow transition-transform',
          checked ? 'translate-x-[1.375rem]' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

// A labeled preference row with a trailing switch. `className` lets callers restyle the row's
// padding when it sits inside a bordered card rather than a plain settings list.
export function PreferenceRow({ title, description, checked, onChange, disabled = false, className }) {
  return (
    <div className={cn('flex items-center justify-between gap-4 py-1', disabled && 'opacity-60', className)}>
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </div>
      <Switch checked={checked} onChange={onChange} label={title} disabled={disabled} />
    </div>
  );
}

// A labeled preference row with a trailing select. `options` is [{ value, label }].
export function SelectPreferenceRow({ title, description, value, options, onChange, disabled = false }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </div>
      <select
        value={value}
        disabled={disabled}
        aria-label={title}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-8 shrink-0 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled ? 'cursor-not-allowed opacity-60' : '',
        )}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// A labeled preference row with a trailing small-integer input (e.g. minutes / hours).
// Uncontrolled + commit-on-blur so typing isn't clamped mid-edit; `key={value}` remounts the
// field whenever the stored (normalized) value changes so a clamped entry re-syncs on display.
export function NumberPreferenceRow({
  title,
  description,
  value,
  onCommit,
  min,
  max,
  unit,
  disabled = false,
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <input
          key={value}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          defaultValue={value}
          disabled={disabled}
          aria-label={title}
          onBlur={(e) => onCommit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          className={cn(
            'h-8 w-20 rounded-md border border-input bg-background px-2 text-right text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            disabled ? 'cursor-not-allowed opacity-60' : '',
          )}
        />
        {unit ? <span className="text-xs text-muted-foreground">{unit}</span> : null}
      </div>
    </div>
  );
}
